import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Typography, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Alert
} from '@mui/material';
import { AutoAwesome as AIIcon, RocketLaunch as RocketIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiService from '../utils/api';

export default function AIGenerateModal({ open, onClose, onGenerated }) {
  const { t, i18n } = useTranslation();
  const [description, setDescription] = useState('');
  const [projectType, setProjectType] = useState('business');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (description.trim().length < 10) {
      setError(t('ai.descriptionTooShort'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await apiService.generateRoadmap(description, projectType, i18n.language);
      onGenerated(result);
      onClose();
      setDescription('');
    } catch (err) {
      setError(err.message || t('ai.generationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, background: 'var(--dialog-bg)' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AIIcon sx={{ color: 'var(--ai-accent)' }} />
        <Typography variant="h6" fontWeight={700}>{t('ai.generateTitle')}</Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('ai.generateDescription')}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          fullWidth
          multiline
          rows={4}
          label={t('ai.projectDescriptionLabel')}
          placeholder={t('ai.projectDescriptionPlaceholder')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={loading}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth>
          <InputLabel>{t('ai.projectTypeLabel')}</InputLabel>
          <Select
            value={projectType}
            onChange={(e) => setProjectType(e.target.value)}
            label={t('ai.projectTypeLabel')}
            disabled={loading}
          >
            <MenuItem value="business">{t('ai.typeBusiness')}</MenuItem>
            <MenuItem value="personal">{t('ai.typePersonal')}</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={loading || description.trim().length < 10}
          startIcon={loading ? <CircularProgress size={18} /> : <RocketIcon />}
          sx={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)' }
          }}
        >
          {loading ? t('ai.generating') : t('ai.generateButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
